import type { LaneRole, ProviderKind } from './types.js';

export type TaskCategory = 'coding' | 'review' | 'debugging' | 'reasoning' | 'docs' | 'tool-use';

export interface SwarmLanePlan {
  role: LaneRole;
  provider: ProviderKind;
  model: string;
  task: string;
  dependencies: LaneRole[];
}

export interface SwarmPlan {
  goal: string;
  taskType: TaskCategory;
  lanes: SwarmLanePlan[];
  handoff: Array<{ from: LaneRole; to: LaneRole; signal: string }>;
}

const KEYWORDS: Record<TaskCategory, string[]> = {
  coding: ['ship', 'implement', 'build', 'add', 'create', 'write', 'feature'],
  review: ['review', 'audit', 'inspect', 'evaluate'],
  debugging: ['fix', 'bug', 'error', 'crash', 'debug', 'repair'],
  reasoning: ['plan', 'think', 'strategy', 'design', 'architect'],
  docs: ['document', 'docs', 'readme', 'explain', 'tutorial'],
  'tool-use': ['automate', 'script', 'pipeline', 'integrate'],
};

export function classifyGoal(goal: string): TaskCategory {
  const lower = goal.toLowerCase();
  for (const [kind, kws] of Object.entries(KEYWORDS) as Array<[TaskCategory, string[]]>) {
    if (kws.some((kw) => lower.includes(kw))) {
      return kind;
    }
  }
  return 'coding';
}

const DEFAULT_PROVIDER: ProviderKind = 'ollama';
const DEFAULT_MODEL = 'qwen2.5-coder:14b';

function lane(role: LaneRole, task: string, dependencies: LaneRole[] = []): SwarmLanePlan {
  return {
    role,
    provider: DEFAULT_PROVIDER,
    model: DEFAULT_MODEL,
    task,
    dependencies,
  };
}

export function planSwarm(goal: string, overrides?: Partial<SwarmLanePlan>): SwarmPlan {
  const taskType = classifyGoal(goal);
  const applyOverride = (input: SwarmLanePlan): SwarmLanePlan => ({ ...input, ...overrides });

  const coordinator = applyOverride(lane('coordinator', `Decompose and route: ${goal}`, []));

  const scout = applyOverride(
    lane('scout', `Map files, symbols, and dependencies relevant to: ${goal}`, ['coordinator']),
  );

  let primary: SwarmLanePlan;
  let secondary: SwarmLanePlan;
  let handoff: SwarmPlan['handoff'];

  switch (taskType) {
    case 'review':
      primary = applyOverride(lane('reviewer', `Audit quality, risk, and tests for: ${goal}`, ['scout']));
      secondary = applyOverride(
        lane('documenter', 'Summarize findings into a reviewable report', ['reviewer']),
      );
      handoff = [
        { from: 'coordinator', to: 'scout', signal: 'map workspace' },
        { from: 'scout', to: 'reviewer', signal: 'context ready' },
        { from: 'reviewer', to: 'documenter', signal: 'findings ready' },
      ];
      break;
    case 'debugging':
      primary = applyOverride(lane('builder', `Reproduce and fix: ${goal}`, ['scout']));
      secondary = applyOverride(
        lane('tester', `Add regression test and verify fix for: ${goal}`, ['builder']),
      );
      handoff = [
        { from: 'coordinator', to: 'scout', signal: 'locate failure' },
        { from: 'scout', to: 'builder', signal: 'context ready' },
        { from: 'builder', to: 'tester', signal: 'fix ready' },
      ];
      break;
    case 'docs':
      primary = applyOverride(lane('documenter', `Write docs for: ${goal}`, ['scout']));
      secondary = applyOverride(lane('reviewer', 'Review docs for clarity and accuracy', ['documenter']));
      handoff = [
        { from: 'coordinator', to: 'scout', signal: 'map docs' },
        { from: 'scout', to: 'documenter', signal: 'outline ready' },
        { from: 'documenter', to: 'reviewer', signal: 'draft ready' },
      ];
      break;
    case 'reasoning':
      primary = applyOverride(
        lane('reviewer', `Produce a plan and tradeoff analysis for: ${goal}`, ['scout']),
      );
      secondary = applyOverride(lane('documenter', 'Capture the decision record', ['reviewer']));
      handoff = [
        { from: 'coordinator', to: 'scout', signal: 'map context' },
        { from: 'scout', to: 'reviewer', signal: 'context ready' },
        { from: 'reviewer', to: 'documenter', signal: 'decision ready' },
      ];
      break;
    case 'tool-use':
      primary = applyOverride(lane('builder', `Automate the pipeline: ${goal}`, ['scout']));
      secondary = applyOverride(lane('reviewer', 'Audit the automation for safety', ['builder']));
      handoff = [
        { from: 'coordinator', to: 'scout', signal: 'map surface' },
        { from: 'scout', to: 'builder', signal: 'surface ready' },
        { from: 'builder', to: 'reviewer', signal: 'pipeline ready' },
      ];
      break;
    default:
      primary = applyOverride(lane('builder', `Implement: ${goal}`, ['scout']));
      secondary = applyOverride(lane('reviewer', `Review implementation of: ${goal}`, ['builder']));
      handoff = [
        { from: 'coordinator', to: 'scout', signal: 'map workspace' },
        { from: 'scout', to: 'builder', signal: 'context ready' },
        { from: 'builder', to: 'reviewer', signal: 'implementation ready' },
      ];
      break;
  }

  return {
    goal,
    taskType,
    lanes: [coordinator, scout, primary, secondary],
    handoff,
  };
}

export interface SwarmCadenceOptions {
  tickMs?: number;
  onLaneState: (laneRole: LaneRole, status: 'running' | 'review' | 'done', note: string) => void;
  onHandoff: (from: LaneRole, to: LaneRole, signal: string) => void;
  onApprovalRequest?: (laneRole: LaneRole, title: string) => void;
}

export function driveSwarm(plan: SwarmPlan, opts: SwarmCadenceOptions): { cancel: () => void } {
  const tick = opts.tickMs ?? 600;
  let cancelled = false;
  const timers: ReturnType<typeof setTimeout>[] = [];

  function schedule(fn: () => void, delay: number) {
    const timer = setTimeout(() => {
      if (!cancelled) fn();
    }, delay);
    timers.push(timer);
  }

  plan.lanes.forEach((lanePlan, index) => {
    const base = tick * (index + 1);
    schedule(() => opts.onLaneState(lanePlan.role, 'running', `${lanePlan.role} lane started`), base);

    if (lanePlan.role === 'builder' || lanePlan.role === 'documenter') {
      schedule(
        () => opts.onApprovalRequest?.(lanePlan.role, `Write changes for ${plan.goal.slice(0, 40)}`),
        base + Math.floor(tick / 2),
      );
    }

    schedule(
      () => opts.onLaneState(lanePlan.role, 'review', `${lanePlan.role} produced output`),
      base + tick,
    );
    schedule(() => opts.onLaneState(lanePlan.role, 'done', `${lanePlan.role} complete`), base + tick * 2);
  });

  plan.handoff.forEach((edge, index) => {
    const delay = tick * (index + 1) + Math.floor(tick / 3);
    schedule(() => opts.onHandoff(edge.from, edge.to, edge.signal), delay);
  });

  return {
    cancel: () => {
      cancelled = true;
      for (const timer of timers) {
        clearTimeout(timer);
      }
    },
  };
}
